import axios from 'axios';
import IntegrityService from '../services/IntegrityService';
import { API_BASE_URL } from './common';

// 라우터 기능을 사용하기 위한 임포트
// 이 변수는 createNavigationHelper 함수에서 설정됨
let navigate = null;

// 토큰 갱신 함수 참조 (AuthContext에서 설정)
let refreshTokenFunction = null;

// 무결성 검증 서비스 인스턴스
const integrityService = new IntegrityService();

// 토큰 갱신 진행 중 여부 추적
let isRefreshing = false;
// 갱신 대기 중인 요청들을 저장하는 배열
let failedQueue = [];

/**
 * 라우터 네비게이션을 설정하는 헬퍼 함수
 * 애플리케이션 초기화 시점에 호출되어야 함
 * 
 * @param {function} navigateFunction - React Router의 navigate 함수
 */
export const createNavigationHelper = (navigateFunction) => {
  navigate = navigateFunction;
};

/**
 * 토큰 갱신 함수를 설정하는 헬퍼 함수
 * AuthContext에서 호출되어야 함
 * 
 * @param {function} refreshFunction - AuthContext의 refreshToken 함수
 */
export const setRefreshTokenFunction = (refreshFunction) => {
  refreshTokenFunction = refreshFunction;
};

/**
 * 대기 중인 요청들을 처리하는 함수
 * 
 * @param {Error|null} error - 토큰 갱신 실패 시 오류
 * @param {string|null} token - 새로운 토큰 (HTTP-Only 쿠키의 경우 null)
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// axios 기본 인스턴스 생성
const axiosInstance = axios.create({
  baseURL: API_BASE_URL, // API 기본 경로 설정
  timeout: 15000, // 타임아웃 설정 (15초)
  withCredentials: true // 쿠키를 자동으로 전송
});

// 요청 인터셉터
axiosInstance.interceptors.request.use(
  (config) => {
    // HttpOnly 쿠키로 JWT 토큰이 전송되므로 추가적인 헤더 설정 필요 없음
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터
axiosInstance.interceptors.response.use(
  async (response) => {
    // 응답이 성공적인 경우 (2xx)
    
    // 무결성 검증 수행
    try {
      const integrityResult = await integrityService.verifyResponseIntegrity(response);
      
      if (!integrityResult.isValid) {
        console.error('API 응답 무결성 검증 실패:', integrityResult.reason);
        
        // 무결성 검증 실패 시 에러로 처리
        const integrityError = new Error('API 응답 무결성 검증에 실패했습니다.');
        integrityError.integrityViolation = true;
        integrityError.reason = integrityResult.reason;
        return Promise.reject(integrityError);
      }
      
      // 무결성 검증 성공 시 디버그 로그
      if (integrityResult.reason === 'VERIFIED') {
        console.debug('API 응답 무결성 검증 성공');
      }
      
    } catch (error) {
      console.warn('무결성 검증 중 오류 발생:', error);
      // 무결성 검증 오류는 경고만 하고 응답은 통과시킴
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // 서버 응답이 있는 경우
    if (error.response) {
      const { status, data } = error.response;

      // 서버에서 쿠키를 삭제했는지 확인 (우선순위)
      const serverClearedCookies = error.response.status === 401 && (
        error.response.headers['x-session-status'] === 'not-found' ||
        error.response.headers['x-auth-action'] === 'cookies-cleared' ||
        error.response.headers['X-Session-Status'] === 'not-found' ||
        error.response.headers['X-Auth-Action'] === 'cookies-cleared'
      );

      if (serverClearedCookies) {
        console.log('✅ 서버에서 쿠키 삭제 완료 - 세션 만료로 인한 자동 로그아웃');
        
        // 강제 로그아웃 처리
        localStorage.removeItem('user');
        
          // 자동 리디렉션 활성화
        setTimeout(() => {
          if (navigate) {
            navigate('/login', { replace: true });
          } else {
            window.location.href = '/login';
          }
        }, 2000);
        
        // 원본 에러를 그대로 던져서 호출부에서 자연스럽게 실패 처리
        return Promise.reject(error);
      }

      // 세션 만료 확인 (context.session_expired가 true인지)
      if (error.response.status === 401 && 
          error.response.data.context && 
          error.response.data.context.session_expired) {

        console.log('세션 만료:', error.response.data);
        
        // 강제 로그아웃 처리
        localStorage.removeItem('user');
        // 로그인 페이지로 리디렉션
        if (navigate) {
          navigate('/login', { replace: true });
        } else {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      // 인증 오류 (401 Unauthorized) - 토큰 갱신 시도
      if (status === 401 && !originalRequest._retry) {
        // 토큰 갱신 함수가 설정되지 않은 경우 즉시 로그인 페이지로
        if (!refreshTokenFunction) {
          console.log('토큰 갱신 함수가 설정되지 않음');
          if (navigate) {
            navigate('/login', { replace: true });
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        // 이미 재시도한 요청인 경우 로그인 페이지로
        if (originalRequest._retry) {
          if (navigate) {
            navigate('/login', { replace: true });
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        // 토큰 갱신이 이미 진행 중인 경우
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => {
            return axiosInstance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          console.log('토큰 갱신 시도 중...');
          const refreshSuccess = await refreshTokenFunction();
          
          if (refreshSuccess) {
            console.log('토큰 갱신 성공');
            processQueue(null);
            isRefreshing = false;
            // 원래 요청 재시도
            return axiosInstance(originalRequest);
          } else {
            console.log('토큰 갱신 실패');
            processQueue(new Error('토큰 갱신 실패'));
            isRefreshing = false;
            // 로그인 페이지로 리디렉션
            if (navigate) {
              navigate('/login', { replace: true });
            } else {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.error('토큰 갱신 중 오류:', refreshError);
          processQueue(refreshError);
          isRefreshing = false;
          // 로그인 페이지로 리디렉션
          if (navigate) {
            navigate('/login', { replace: true });
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      }
      
      // 권한 부족 오류 (403 Forbidden) - 토큰 만료 가능성도 체크
      else if (status === 403 && !originalRequest._retry) {
        // 토큰 만료로 인한 403일 가능성이 있으므로 토큰 갱신 시도
        console.log('403 에러 발생 - 토큰 만료 가능성으로 갱신 시도');
        
        // 토큰 갱신 함수가 설정되지 않은 경우 즉시 로그인 페이지로
        if (!refreshTokenFunction) {
          console.log('토큰 갱신 함수가 설정되지 않음');
          if (navigate) {
            navigate('/login', { replace: true });
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        // 토큰 갱신이 이미 진행 중인 경우
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => {
            return axiosInstance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          console.log('403 에러로 인한 토큰 갱신 시도 중...');
          const refreshSuccess = await refreshTokenFunction();
          
          if (refreshSuccess) {
            console.log('토큰 갱신 성공 - 원래 요청 재시도');
            processQueue(null);
            isRefreshing = false;
            // 원래 요청 재시도
            return axiosInstance(originalRequest);
          } else {
            console.log('토큰 갱신 실패 - 로그인 페이지로 이동');
            processQueue(new Error('토큰 갱신 실패'));
            isRefreshing = false;
            // 로그인 페이지로 리디렉션
            if (navigate) {
              navigate('/login', { replace: true });
            } else {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.error('403 에러 토큰 갱신 중 오류:', refreshError);
          processQueue(refreshError);
          isRefreshing = false;
          // 로그인 페이지로 리디렉션
          if (navigate) {
            navigate('/login', { replace: true });
          } else {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      }
      // 이미 재시도한 403 에러는 실제 권한 부족으로 처리
      else if (status === 403 && originalRequest._retry) {
        console.log('실제 권한 오류: 접근 권한이 없습니다.');
        // 선택적: 권한 없음 페이지로 리디렉션
        // if (navigate) navigate('/unauthorized', { replace: true });
      }
      
      // 리소스 없음 (404 Not Found)
      else if (status === 404) {
        console.log('요청한 리소스를 찾을 수 없습니다.');
      }
      
      // 서버 오류 (500 Internal Server Error)
      else if (status >= 500) {
        console.log('서버 오류가 발생했습니다.');
      }
      
      // 기타 오류
      else {
        console.log(`오류 발생: ${status} - ${data.message || '알 수 없는 오류'}`);
      }
    }
    // 요청 설정 문제 (네트워크 오류, CORS 등)
    else if (error.request) {
      console.log('서버에 응답이 없습니다. 네트워크를 확인하세요.');
    }
    // 기타 오류
    else {
      console.log('요청 중 오류가 발생했습니다:', error.message);
    }
    
    // Promise.reject로 오류를 반환하여 호출자가 catch 블록에서 처리할 수 있게 함
    return Promise.reject(error);
  }
);

export default axiosInstance;
